import React from "react";
import {ActionType, BaseComponent, ch} from "@reapptor-apps/reapptor-react-common";
import {CellAction, CellModel, ColumnActionDefinition, ColumnActionType, ColumnDefinition, ColumnType, Grid, GridHoveringType, GridModel, GridOddType, RowModel, SelectListItem, Tab, TabContainer} from "@reapptor-apps/reapptor-react-components";
import CruiseArea from "@/models/server/CruiseArea";
import SaveAreaRequest from "@/models/server/requests/SaveAreaRequest";
import SaveAreaResponse from "@/models/server/responses/SaveAreaResponse";
import AreaPricesPanel from "@/pages/CruiseCountryManagement/AreaPricesPanel/AreaPricesPanel";
import EnumProvider from "@/providers/EnumProvider";
import Localizer from "../../../localization/Localizer";

import styles from "./AreasPanel.module.scss";

interface IAreasPanelProps {
    countryId: string;
}

interface IAreasPanelState {
}

export default class AreasPanel extends BaseComponent<IAreasPanelProps, IAreasPanelState> {

    state: IAreasPanelState = {
    };

    private readonly _areasGridRef: React.RefObject<Grid<CruiseArea>> = React.createRef();

    private readonly _areasColumns: ColumnDefinition[] = [
        {
            header: "#",
            minWidth: 50,
            noWrap: true,
            className: "grey",
            accessor: "#"
        } as ColumnDefinition,
        {
            header: Localizer.areasPanelNameColumnLanguageItemName,
            name: "name",
            accessor: nameof<CruiseArea>(o => o.name),
            minWidth: 300,
            maxWidth: 300,
            type: ColumnType.Text
        } as ColumnDefinition,
        {
            group: Localizer.areasPanelPriceGroupLanguageItemName,
            header: Localizer.areasPanelEstimateColumnLanguageItemName,
            accessor: nameof<CruiseArea>(o => o.estimatedPrice),
            minWidth: 70,
            maxWidth: 70,
            type: ColumnType.Boolean,
            reRenderRow: true,
            init: (cell) => this.initEstimateOperations(cell),
            callback: (cell) => this.setEstimatedAsync(cell),
            actions: [
                {
                    name: "toggle",
                    title: Localizer.areasPanelTogglePrices,
                    type: ColumnActionType.Details,
                    callback: async (cell) => await this.togglePricesAsync(cell)
                } as ColumnActionDefinition
            ]
        } as ColumnDefinition,
        {
            group: Localizer.areasPanelPriceGroupLanguageItemName,
            header: Localizer.areasPanelBuffetColumnLanguageItemName,
            accessor: nameof<CruiseArea>(o => o.buffetPrice),
            minWidth: 70,
            maxWidth: 70,
            format: "0.00",
            type: ColumnType.Number,
            init: (cell) => this.initPriceColumns(cell),
        } as ColumnDefinition,
        {
            group: Localizer.areasPanelPriceGroupLanguageItemName,
            header: Localizer.areasPanelSnackColumnLanguageItemName,
            accessor: nameof<CruiseArea>(o => o.snacksPrice),
            minWidth: 70,
            maxWidth: 70,
            format: "0.00",
            type: ColumnType.Number,
            init: (cell) => this.initPriceColumns(cell),
        } as ColumnDefinition,
        {
            group: Localizer.areasPanelPriceGroupLanguageItemName,
            header: Localizer.areasPanelAlcoholColumnLanguageItemName,
            accessor: nameof<CruiseArea>(o => o.alcoholPrice),
            minWidth: 70,
            maxWidth: 70,
            format: "0.00",
            type: ColumnType.Number,
            init: (cell) => this.initPriceColumns(cell),
        } as ColumnDefinition,
        {
            group: Localizer.areasPanelPriceGroupLanguageItemName,
            header: Localizer.areasPanelFishingEquipmentColumnLanguageItemName,
            accessor: nameof<CruiseArea>(o => o.fishingEquipmentPrice),
            minWidth: 70,
            maxWidth: 70,
            format: "0.00",
            type: ColumnType.Number,
            init: (cell) => this.initPriceColumns(cell),
        } as ColumnDefinition,
        {
            header: Localizer.areasPanelActionsColumnLanguageItemName,
            minWidth: 140,
            removable: false,
            init: (cell) => this.initAreaOperations(cell),
            actions: [
                {
                    name: "save",
                    title: Localizer.genericSave,
                    icon: "far save",
                    type: ActionType.Create,
                    callback: async (cell, action) => await this.processAreaOperationAsync(cell, action)
                } as ColumnActionDefinition,
                {
                    name: "cancel",
                    title: Localizer.genericCancel,
                    icon: "far ban",
                    type: ActionType.Delete,
                    callback: async (cell, action) => await this.processAreaOperationAsync(cell, action)
                } as ColumnActionDefinition,
                {
                    name: "delete",
                    title: Localizer.genericDelete,
                    icon: "far trash-alt",
                    type: ActionType.Delete,
                    right: true,
                    confirm: Localizer.areasPanelDeleteConfirmation,
                    callback: async (cell, action) => await this.processAreaOperationAsync(cell, action)
                } as ColumnActionDefinition
            ]
        } as ColumnDefinition,
    ];

    private async getAreasAsync(): Promise<CruiseArea[]> {
        return await this.areasGrid.postAsync("api/cruise/getCountryAreas", this.countryId);
    }

    private async togglePricesAsync(cell: CellModel<CruiseArea>): Promise<void> {
        await cell.row.toggleAsync();
    }

    private isValid(model: CruiseArea): boolean {
        let isValid = (!!model.name);
        isValid = isValid && (!this.areasGrid.items.some(item => ((item.id != model.id) && (item.name == model.name))));
        return isValid;
    }

    private initPriceColumns(cell: CellModel<CruiseArea>): void {
        const model: CruiseArea = cell.row.model;
        cell.visible = model.estimatedPrice;
    }

    private initEstimateOperations(cell: CellModel<CruiseArea>): void {
        const model: CruiseArea = cell.row.model;
        
        const toggleAction: CellAction<CruiseArea> = cell.actions[0];

        cell.className = styles.estimated;
        toggleAction.visible = model.estimatedPrice;
    }

    private async setEstimatedAsync(cell: CellModel<CruiseArea>): Promise<void> {
        const model: CruiseArea = cell.row.model;
        
        if ((cell.row.expanded) && (!model.estimatedPrice)) {
            await cell.row.toggleAsync()
        }
    }

    private initAreaOperations(cell: CellModel<CruiseArea>): void {
        const model: CruiseArea = cell.row.model;
        const modified: boolean = cell.row.modified;
        const deleted: boolean = cell.row.deleted;

        const isValid: boolean = this.isValid(model);

        const saveAction: CellAction<CruiseArea> = cell.actions[0];
        const cancelAction: CellAction<CruiseArea> = cell.actions[1];
        const deleteAction: CellAction<CruiseArea> = cell.actions[2];

        saveAction.visible = (modified) && (isValid) && (!deleted);
        cancelAction.visible = (modified) && (!deleted);
        deleteAction.visible = (!deleted);
    }

    private async processAreaOperationAsync(cell: CellModel<CruiseArea>, action: CellAction<CruiseArea>): Promise<void> {
        const model: CruiseArea = cell.model;
        const isNew: boolean = (!model.id);

        if (action.action.name === "save") {

            const saveAreaRequest = new SaveAreaRequest();
            saveAreaRequest.id = model.id;
            saveAreaRequest.countryId = this.countryId;
            saveAreaRequest.name = model.name!;
            saveAreaRequest.estimatedPrice = model.estimatedPrice;
            saveAreaRequest.buffetPrice = model.buffetPrice;
            saveAreaRequest.snacksPrice = model.snacksPrice;
            saveAreaRequest.alcoholPrice = model.alcoholPrice;
            saveAreaRequest.fishingEquipmentPrice = model.fishingEquipmentPrice;
            
            const response: SaveAreaResponse = await cell.grid.postAsync("api/cruise/saveArea", saveAreaRequest);
            
            if (response.alreadyExists) {
                await ch.alertErrorAsync(Localizer.areasPanelAreaAlreadyExists.format(model.name));
                return;
            }

            cell.row.model = response.area!;

            await cell.row.bindAsync();

        } else if (action.action.name === "cancel") {

            await cell.row.cancelAsync();

        } else if (action.action.name === "delete") {
            
            if (isNew) {
                await cell.grid.deleteAsync(cell.row.index);
            } else {
                const deleted: boolean = await cell.grid.postAsync("api/cruise/deleteArea", model.id);
                
                if (!deleted) {
                    await ch.alertErrorAsync(Localizer.areasPanelAreaCannotBeDeleted.format(model.name));
                    return;
                }
                
                await cell.grid.reloadAsync();
            }
        }
    }

    private get countryId(): string  {
        return this.props.countryId;
    }

    public get areasGrid(): GridModel<CruiseArea> {
        return this._areasGridRef.current!.model;
    }
    
    public async reloadAsync(): Promise<void> {
        await this.areasGrid.reloadAsync();
    }

    public async addAreaAsync(): Promise<void> {
        const area = new CruiseArea();
        area.countryId = this.countryId;

        const newRows: RowModel<CruiseArea>[] = await this.areasGrid.insertAsync(0, area);

        const newRow: RowModel<CruiseArea> = newRows[0];
        const nameCell: CellModel<CruiseArea> = newRow.get("name");

        await nameCell.editAsync(true);
    }
    
    public renderDetails(row: RowModel<CruiseArea>): React.ReactNode {
        const model: CruiseArea = row.model;
        const groups: SelectListItem[] = EnumProvider.getCruiseGroupSizeItems();
        return (
            <TabContainer>

                <Tab id={"Generic"} title={"Generic"}>

                    <AreaPricesPanel area={model} groupSize={null}/>

                </Tab>

                {
                    groups.map(item =>
                        <Tab key={item.value} id={item.value} title={item.text}>

                            <AreaPricesPanel area={model} groupSize={parseInt(item.value)}/>

                        </Tab>
                    )
                }

            </TabContainer>
        )
    }

    public render(): React.ReactNode {
        return (
            <React.Fragment>

                <div className={styles.areasPanel}>
    
                    <Grid ref={this._areasGridRef} autoToggle
                          className={this.css(styles.grid)}
                          hovering={GridHoveringType.Row}
                          odd={GridOddType.None}
                          minWidth="auto"
                          noDataText={Localizer.areasPanelNoAreas}
                          columns={this._areasColumns}
                          renderDetails={(row) => this.renderDetails(row)}
                          fetchData={() => this.getAreasAsync()}
                    />
                    
                </div>

            </React.Fragment>
        );
    }
}